import React, { FC, useCallback, useState } from "react";
import { Stack, TextField, Typography } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FormikHandlers } from "formik";
import { CommandSnippet } from "@/components/pages/common/connectors/components/CommandSnippet";
import { SnowflakeConnectionFormInfoCard } from "@/components/pages/integrations/components/provider-connections/snowflake/components/drawer/form/SnowflakeConnectionFormInfoCard";

interface SnowflakeServiceAccountFieldProps {
  serviceAccount: string;
  handleChange: FormikHandlers["handleChange"];
  error?: boolean;
}

export const SnowflakeServiceAccountField: FC<
  SnowflakeServiceAccountFieldProps
> = ({ serviceAccount, handleChange, error }) => {
  const [
    isServiceAccountDescriptionCollapsed,
    setIsServiceAccountDescriptionCollapsed,
  ] = useState(true);

  const handleFieldDescriptionCollapse = useCallback(() => {
    setIsServiceAccountDescriptionCollapsed((prev) => !prev);
  }, []);

  return (
    <Stack spacing={1}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography>Service Account</Typography>
        <Tooltip
          title="Snowflake automatically associates the storage integration with a Cloud Storage service account created for your account. Snowflake creates a single service account that is referenced by all GCS storage integrations in your Snowflake account."
          placement="top"
          arrow
        >
          <InfoOutlinedIcon color="action" sx={{ fontSize: 16 }} />
        </Tooltip>
      </Stack>
      <TextField
        fullWidth
        value={serviceAccount ?? ""}
        type="text"
        size="small"
        name="service_account"
        error={error}
        onChange={handleChange}
      />

      <SnowflakeConnectionFormInfoCard
        title={
          <Typography
            variant="body2"
            width="100%"
            display="block"
            color="text.secondary"
          >
            How to find the{" "}
            <Typography variant="inherit" component="span" fontWeight="medium">
              Service Account
            </Typography>
            ?
          </Typography>
        }
        description={
          <CommandSnippet
            code={`DESC INTEGRATION cloudchipr_data; SELECT "property", "property_value" FROM TABLE(RESULT_SCAN(LAST_QUERY_ID())) WHERE "property" = 'STORAGE_GCP_SERVICE_ACCOUNT';`}
            parentBoxSx={{ m: 0 }}
          >
            <Typography
              sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
              variant="body2"
            >
              DESC INTEGRATION cloudchipr_data; <br />
              SELECT "property", "property_value" FROM
              TABLE(RESULT_SCAN(LAST_QUERY_ID())) <br />
              WHERE "property" = 'STORAGE_GCP_SERVICE_ACCOUNT'; <br />
            </Typography>
          </CommandSnippet>
        }
        isCollapsed={isServiceAccountDescriptionCollapsed}
        handleCollapse={handleFieldDescriptionCollapse}
      />
    </Stack>
  );
};
